import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { WizardButtonsConfig } from "views/uikit/VLWizard";
import { useCallback, useMemo } from "react";
import useCreateDataset from "hooks/useCreateDataset";
import {
  getCreationStatus,
  getCreationStatusPolling,
  getFolderMetadata,
} from "redux/CreateDataset/selectors";
import { processDataset } from "redux/CreateDataset/actions";
import { toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import { DatasetStatusEnum, EDatasetStatus } from "../../../../types";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import { NEW_STATUS } from "../../../../helpers/constants/filters";

interface UseDatasetWizardNavigation {
  currentStep: number;
  selectedFolderIds: string[];
  isFolderUploading: boolean;
  /** Scan-result exports still queued or running on the Add Data step. */
  pendingExports?: number;
  setCurrentStep: (step: number) => void;
  isUpdateMode?: boolean;
  batchId?: string;
}

const useDatasetWizardNavigation = ({
  currentStep,
  selectedFolderIds,
  isFolderUploading,
  pendingExports = 0,
  setCurrentStep,
  isUpdateMode = false,
  batchId,
}: UseDatasetWizardNavigation): WizardButtonsConfig => {
  const { selectFolders } = useCreateDataset();
  const { newDatasetId } = useParams();
  const dispatch = useDispatch();
  const folderMetadata = useSelector(getFolderMetadata);
  const creationStatus = useSelector(getCreationStatus);
  const polling = useSelector(getCreationStatusPolling);
  const navigate = useNavigate();
  const displayNewStatus = useFeatureFlagService(NEW_STATUS).show;

  // Action functions
  const handleGoToPreview = useCallback(async () => {
    try {
      const isSuccess = await selectFolders(selectedFolderIds, batchId);
      if (!isSuccess) return;

      setCurrentStep(1);
    } catch (error: any) {
      toast(
        <VLToastComponent
          title="Folder Selection Error"
          status="ERROR"
          description={error.message || "Failed to select folders"}
        />,
      );
    }
  }, [selectFolders, selectedFolderIds, setCurrentStep, batchId]);

  const handleGoToPrevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, setCurrentStep]);

  const handleCreateDataset = useCallback(async () => {
    if (!newDatasetId) return;

    try {
      // Call the /process endpoint
      const success = await dispatch(
        processDataset(newDatasetId, batchId) as any,
      );

      if (success) {
        // Navigate to CreateDataset step (step 2) - polling will be handled automatically
        setCurrentStep(2);
      }
    } catch (error) {
      toast(
        <VLToastComponent
          title="Dataset Processing Error"
          status="ERROR"
          description="Something went wrong while processing your dataset. Please try again later."
        />,
      );
      console.error("Failed to process dataset:", error);
    }
  }, [batchId, dispatch, newDatasetId, setCurrentStep]);

  const handleFinish = useCallback(() => {
    navigate(`/dataset/${newDatasetId}/data`);
  }, [navigate, newDatasetId]);

  const allSelectedAreReady = useMemo(() => {
    const readyFolderIds = folderMetadata
      .filter((f) => f.status === "valid")
      .map((f) => f.id);

    return selectedFolderIds.every((id) => readyFolderIds.includes(id));
  }, [selectedFolderIds, folderMetadata]);

  const disabledShowPreviewText = useMemo(() => {
    const reasons = [
      {
        // First: until they land, the folders the other reasons talk about may not
        // exist yet.
        condition: pendingExports > 0,
        message: `please wait for ${pendingExports} scan result export${pendingExports === 1 ? "" : "s"} to finish`,
      },
      {
        condition: folderMetadata.length === 0,
        message: "please upload at least one folder",
      },
      {
        condition: selectedFolderIds.length === 0,
        message: "please select at least one folder",
      },
      {
        condition: isFolderUploading && !allSelectedAreReady,
        message: "please wait for the folder to be uploaded",
      },
      {
        condition: !allSelectedAreReady,
        message: "please wait for selected folders to be ready",
      },
    ];

    return reasons.find((r) => r.condition)?.message || "";
  }, [
    folderMetadata,
    selectedFolderIds,
    isFolderUploading,
    allSelectedAreReady,
    pendingExports,
  ]);

  const disabledFinishText = useMemo(() => {
    if (creationStatus && creationStatus.status === "READY") {
      return "";
    }

    if (polling) {
      return isUpdateMode
        ? "Please wait for the media to be added."
        : "Please wait for the dataset to be created.";
    }

    if (displayNewStatus) {
      if (creationStatus?.status_new === EDatasetStatus.ERROR) {
        return (
          creationStatus.fatalError ||
          (isUpdateMode ? "Adding media failed." : "Dataset creation failed.")
        );
      }
    } else {
      if (creationStatus?.status === DatasetStatusEnum.FATAL_ERROR) {
        return (
          creationStatus.fatalError ||
          (isUpdateMode ? "Adding media failed." : "Dataset creation failed.")
        );
      }
    }

    return "";
  }, [creationStatus, polling, displayNewStatus, isUpdateMode]);

  return useMemo(() => {
    const stepConfigs: Record<number, WizardButtonsConfig> = {
      0: {
        // Add Data step
        showNext: true,
        showPrev: false,
        disableNextTooltip: disabledShowPreviewText,
        disablePrevTooltip: "no previous step",
        nextText: "Continue to Preview",
        prevText: "Previous",
        onNext: handleGoToPreview,
      },
      1: {
        // Preview step
        showNext: true,
        showPrev: true,
        disableNextTooltip: "",
        disablePrevTooltip: "",
        nextText: isUpdateMode ? "Add Media" : "Create Dataset",
        prevText: "Back",
        onNext: handleCreateDataset,
        onPrev: handleGoToPrevStep,
      },
      2: {
        // Create Dataset step
        showNext: true,
        showPrev: false,
        disableNextTooltip: disabledFinishText,
        disablePrevTooltip: "",
        nextText: "Explore Dataset",
        prevText: "Previous",
        onNext: handleFinish,
      },
    };

    return stepConfigs[currentStep];
  }, [
    currentStep,
    disabledFinishText,
    disabledShowPreviewText,
    handleGoToPreview,
    handleGoToPrevStep,
    handleCreateDataset,
    handleFinish,
    isUpdateMode,
  ]);
};

export default useDatasetWizardNavigation;
